import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  LogIn, LogOut, Search, Eye, EyeOff, Copy, Edit, Trash, Plus, Check, Save, UserPlus, Users, Briefcase, Key, X, Link, ChevronDown, ChevronUp, Lock, Shield, Menu
} from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import AuthCallback from './AuthCallback';
import LoadingButton from './LoadingButton';


// API Endpoint URL
const API_URL = process.env.REACT_APP_API_URL || 'https://password.bitaccounting.com/api.php';
const UPDATE_CHECK_INTERVAL = 1000; // Check every 30 seconds
const BACKGROUND_UPDATE_CHECK_INTERVAL = 120000;

// Enhanced Logo Animation Component with tagline
function LogoAnimation({ onAnimationComplete }) {
  const [animationStage, setAnimationStage] = useState('enter'); // 'enter', 'hold', 'exit'
  
  useEffect(() => {
    // Animation sequence:
    // 1. Enter animation (600ms)
    // 2. Hold visible (1200ms) 
    // 3. Exit animation (600ms)
    const enterTimer = setTimeout(() => {
      setAnimationStage('hold');
      
      const holdTimer = setTimeout(() => {
        setAnimationStage('exit');
        
        const exitTimer = setTimeout(() => {
          onAnimationComplete();
        }, 600);
        
        return () => clearTimeout(exitTimer);
      }, 1200);
      
      return () => clearTimeout(holdTimer);
    }, 100); // Short delay before starting animation
    
    return () => clearTimeout(enterTimer);
  }, [onAnimationComplete]);
  
  // Calculate transform and opacity based on animation stage
  const getAnimationStyle = () => {
    switch(animationStage) {
      case 'enter':
        return { 
          transform: 'translateY(80px) scale(0.9) rotate(-5deg)', 
          opacity: 0,
          filter: 'blur(10px)'
        };
      case 'hold':
        return { 
          transform: 'translateY(0) scale(1) rotate(0deg)', 
          opacity: 1,
          filter: 'blur(0px)'
        };
      case 'exit':
        return { 
          transform: 'translateY(-60px) scale(1.05) rotate(2deg)', 
          opacity: 0,
          filter: 'blur(5px)'
        };
      default:
        return { transform: 'translateY(0) scale(1)', opacity: 1 };
    }
  };
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 z-50 flex items-center justify-center overflow-hidden">
      <div 
        style={{
          ...getAnimationStyle(),
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
        className="flex flex-col items-center justify-center"
      >
        {/* Animated Key Icon */}
        <div 
          className="relative mb-6"
          style={{
            animation: animationStage === 'hold' ? 'float 3s ease-in-out infinite' : 'none'
          }}
        >
          <div className="h-24 w-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-indigo-500/30">
            <Key className="text-white" size={48} />
          </div>
          
          {/* Subtle glow effect */}
          <div className="absolute inset-0 bg-indigo-500 rounded-2xl opacity-20 blur-xl animate-pulse"></div>
        </div>
        
        {/* App Name with Gradient Text */}
        <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent mb-3 text-center tracking-tight">
          Password Vault
        </h1>
        
        {/* Tagline with subtle animation */}
        <p 
          className="text-blue-200 text-sm font-light tracking-wide text-center max-w-xs opacity-0"
          style={{
            opacity: animationStage === 'hold' ? 1 : 0,
            transition: 'opacity 0.8s ease-in-out',
            animation: animationStage === 'hold' ? 'fadeIn 1s ease-in-out' : 'none'
          }}
        >
          Secure Credential Management System
        </p>
        
        {/* Loading dots during hold phase */}
        {animationStage === 'hold' && (
          <div className="flex space-x-1.5 mt-6">
            {[0, 1, 2].map((dot) => (
              <div
                key={dot}
                className="h-2 w-2 bg-indigo-400 rounded-full"
                style={{
                  animation: `bounce 1.2s infinite ${dot * 0.2}s`
                }}
              ></div>
            ))}
          </div>
        )}
      </div>
      
      {/* Inline styles for animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          
          @keyframes bounce {
            0%, 100% { 
              transform: scale(1);
              opacity: 0.6;
            }
            50% { 
              transform: scale(1.2);
              opacity: 1;
            }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
}

// Helper function for the "copy to clipboard" feature
function copyToClipboard(text, type) {
  navigator.clipboard.writeText(text).then(() => {
    console.log(`${type} copied to clipboard!`);
  }).catch(err => {
    console.error('Failed to copy text: ', err);
  });
}

// Main App component that handles user state and data
function AppContent() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwords, setPasswords] = useState([]);
  const [personalPasswords, setPersonalPasswords] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [error, setError] = useState('');
  const [lastUpdateCheck, setLastUpdateCheck] = useState(new Date().toISOString());
  const [updateCheckInterval, setUpdateCheckInterval] = useState(null);
  const [apiLoading, setApiLoading] = useState({
    getPasswords: false,
    addPassword: false,
    updatePassword: false,
    deletePassword: false,
    getUsers: false,
    updateUser: false,
    deleteUser: false,
    assignUsers: false,
    checkUpdates: false,
    getUpdates: false
  });

   // Helper function to set loading state
  const setLoading = (key, value) => {
    setApiLoading(prev => ({ ...prev, [key]: value }));
  };

  const [showIntro, setShowIntro] = useState(() => {
  // ✅ check sessionStorage first
  return !sessionStorage.getItem("intro_shown");
});

// Add this function to handle animation completion
const handleAnimationComplete = () => {
  setShowIntro(false);
  sessionStorage.setItem("intro_shown", "true"); 
};

// Fetch all passwords for admin view - MOVED UP
  const fetchAllPasswords = useCallback(async () => {
    setLoading('getPasswords', true);
    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'get_passwords',
        userId: null,
        userRole: 'admin',
        teamLeadId: null
      });
      setPasswords(response.data.passwords || []);
      setPersonalPasswords(response.data.personal_passwords || []);
    } catch (error) {
      console.error("Error fetching all passwords:", error);
    } finally {
      setLoading('getPasswords', false);
      setIsLoading(false);
    }
  }, []);

  // Fetch passwords for non-admin users - MOVED UP
const fetchPasswords = useCallback(async () => {
  if (!currentUser) return;
  
  setLoading('getPasswords', true);
  setIsLoading(true);
  try {
    const response = await axios.post(API_URL, {
      action: 'get_passwords',
      userId: currentUser.id,
      userRole: currentUser.role,
      teamLeadId: currentUser.role === 'team_lead' ? currentUser.id : null
    });
    
    if (response.data && response.data.success) {
      // The API should already filter properly, but we can double-check for members
      let filteredPasswords = response.data.passwords || [];
      let filteredPersonal = response.data.personal_passwords || [];
      
      // Additional client-side filtering for members (safety net)
      if (currentUser.role === 'member') {
        filteredPasswords = filteredPasswords.filter(p => 
          p && (p.user_id === currentUser.id || 
          (p.assigned_users && p.assigned_users.some(u => u.id === currentUser.id)))
        );
        filteredPersonal = filteredPersonal.filter(p => 
          p && p.user_id === currentUser.id
        );
      }
      
      setPasswords(filteredPasswords);
      setPersonalPasswords(filteredPersonal);
      setLastUpdateCheck(new Date().toISOString());
    } else {
      setError(response.data?.message || "Failed to fetch passwords");
    }
  } catch (error) {
    console.error("Error fetching passwords:", error);
    setError("Error loading passwords: " + (error.response?.data?.message || error.message));
  } finally {
    setLoading('getPasswords', false);
    setIsLoading(false);
  }
}, [currentUser]);
  // Check for Microsoft auth callback on component mount
   useEffect(() => {
    // ✅ restore user instantly
    const savedUser = sessionStorage.getItem("currentUser");
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setCurrentUser(parsedUser);

      // Fetch passwords async (don't block render)
      if (parsedUser.role === "admin") {
        fetchAllPasswords();
      } else if (parsedUser.role === "team_lead") {
        fetchPasswords();
      } else {
        fetchPasswords();
      }
    }

    // Run in parallel
    Promise.all([
      fetchUsersAndTeams(),
      handleStoredMicrosoftAuth()
    ]).catch(err => console.error(err));
  }, []);

// Add this useEffect for update checking
useEffect(() => {
  // Only run global update checking for admin and team_lead users
  // Member users will use their own local update checking system
  if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'team_lead')) {
    // Set up interval for checking updates
    const interval = setInterval(() => {
      checkForUpdates();
    }, UPDATE_CHECK_INTERVAL);
    
    setUpdateCheckInterval(interval);
    
    // Set up visibility change listener to reduce frequency when tab is not visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, reduce check frequency
        clearInterval(interval);
        const backgroundInterval = setInterval(() => {
          checkForUpdates();
        }, BACKGROUND_UPDATE_CHECK_INTERVAL);
        setUpdateCheckInterval(backgroundInterval);
      } else {
        // Tab is visible, use normal frequency
        clearInterval(interval);
        const foregroundInterval = setInterval(() => {
          checkForUpdates();
        }, UPDATE_CHECK_INTERVAL);
        setUpdateCheckInterval(foregroundInterval);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  } else {
    // Clear interval if user logs out or is a member
    if (updateCheckInterval) {
      clearInterval(updateCheckInterval);
      setUpdateCheckInterval(null);
    }
  }
}, [currentUser]);

// Add the checkForUpdates function
const checkForUpdates = async () => {
  if (!currentUser || !lastUpdateCheck) return;
  
  try {
    const response = await axios.post(API_URL, {
      action: 'check_updates',
      lastCheck: lastUpdateCheck,
      userId: currentUser.id,
      userRole: currentUser.role,
      teamLeadId: currentUser.role === 'team_lead' ? currentUser.id : null
    });
    
    if (response.data && response.data.success) {
      // Only fetch updates if there are actual changes
      if (response.data.updates && response.data.updates.length > 0) {
        console.log('Updates detected, refreshing data...');
        
        // Use the appropriate fetch function based on user role
        if (currentUser.role === 'admin') {
          await fetchAllPasswords();
        } else {
          await fetchPasswords();
        }
        
        // Also refresh users if needed
        const hasUserUpdates = response.data.updates.some(update => 
          update.table_name === 'users'
        );
        
        if (hasUserUpdates) {
          await fetchUsersAndTeams();
        }
      }
      
      // Update the timestamp
      setLastUpdateCheck(response.data.currentTime || new Date().toISOString());
    }
  } catch (error) {
    console.error("Error checking for updates:", error);
  }
};


// Update the fetchUpdatedData function
const fetchUpdatedData = useCallback(async () => {
  if (!currentUser) return;
  
  try {
    const response = await axios.post(API_URL, {
      action: 'get_updates',
      lastCheck: lastUpdateCheck,
      userId: currentUser.id,
      userRole: currentUser.role,
      teamLeadId: currentUser.role === 'team_lead' ? currentUser.id : null
    });
  
    if (response.data && response.data.success) {
      setPasswords(response.data.passwords || []);
      setPersonalPasswords(response.data.personal_passwords || []);
      
      if (response.data.users && response.data.users.length > 0) {
        setUsers(response.data.users);
      }
      if (response.data.team_leads && response.data.team_leads.length > 0) {
        setTeamLeads(response.data.team_leads);
      }
      
      setLastUpdateCheck(response.data.currentTime || new Date().toISOString());
    }
  } catch (error) {
    console.error("Error fetching updated data:", error);
    // Implement retry logic or show a subtle notification
  }
}, [currentUser, lastUpdateCheck]);

  const handleStoredMicrosoftAuth = async () => {
    const code = localStorage.getItem('microsoft_auth_code');
    const state = localStorage.getItem('microsoft_auth_state');
    const authTime = localStorage.getItem('microsoft_auth_time');
    const error = localStorage.getItem('microsoft_auth_error');
    const errorDescription = localStorage.getItem('microsoft_auth_error_description');

    // Clean up storage regardless of outcome
    const cleanup = () => {
      localStorage.removeItem('microsoft_auth_code');
      localStorage.removeItem('microsoft_auth_state');
      localStorage.removeItem('microsoft_auth_time');
      localStorage.removeItem('microsoft_auth_error');
      localStorage.removeItem('microsoft_auth_error_description');
    };

    // Check if we have a recent authentication code
    if (code && state && authTime) {
      const timeDiff = new Date().getTime() - parseInt(authTime);
      
      // Only process if the code is recent (within 5 minutes)
      if (timeDiff < 300000) {
        setIsLoading(true);
        
        try {
          const redirectUri = process.env.REACT_APP_MS_REDIRECT_URI || "https://password.bitaccounting.com/auth-callback";
          const response = await axios.post(API_URL, {
            action: 'microsoft_sso',
            code: code,
            redirect_uri: redirectUri
          });
          
          if (response.data.success) {
            handleLogin(response.data.user);
          } else {
            setError(response.data.message || "Microsoft login failed");
          }
        } catch (error) {
          setError("Microsoft login failed. Please try again.");
          console.error("Microsoft login error:", error);
        } finally {
          setIsLoading(false);
          cleanup();
        }
      } else {
        // Code is too old
        setError("Authentication session expired. Please try again.");
        cleanup();
      }
    } else if (error) {
      // Handle authentication error
      setError(`Microsoft authentication error: ${errorDescription || error}`);
      cleanup();
    }

    // Also check if we have stored loading state from redirect
    const wasLoading = localStorage.getItem('microsoft_auth_loading');
    if (wasLoading) {
      setIsLoading(false);
      localStorage.removeItem('microsoft_auth_loading');
    }
  };

  const cleanupAuthStorage = () => {
    localStorage.removeItem('microsoft_auth_state');
    localStorage.removeItem('microsoft_auth_redirect');
    localStorage.removeItem('microsoft_auth_loading');
  };

  const fetchUsersAndTeams = useCallback(async () => {
  setLoading('getUsers', true);
  try {
    const response = await axios.post(API_URL, { 
      action: 'get_users_and_teams',
      lastCheck: new Date(0).toISOString() // Force fetch all data
    });
    if (response.data && response.data.success) {
      setUsers(response.data.users || []);
      setTeamLeads(response.data.team_leads || []);
    }
  } catch (error) {
    console.error("Error fetching initial data:", error);
  } finally {
    setLoading('getUsers', false);
  }
});

  const handleLogin = useCallback((user) => {
    setCurrentUser(user);
    sessionStorage.setItem("currentUser", JSON.stringify(user));
    if (user.role === "admin") {
      fetchAllPasswords();
    } else if (user.role === "team_lead") {
      fetchPasswords();
    } else {
      fetchPasswords();
    }
  }, [fetchAllPasswords, fetchPasswords]);

  const handleRoleChange = useCallback((updatedUser) => {
  if (updatedUser.id === currentUser.id) {
    // Update current user state
    setCurrentUser(updatedUser);
    
    // Update session storage
    sessionStorage.setItem("currentUser", JSON.stringify(updatedUser));
    
    // Clear existing data
    setPasswords([]);
    setPersonalPasswords([]);
    
    // Fetch data based on new role
    if (updatedUser.role === "admin") {
      fetchAllPasswords();
    } else if (updatedUser.role === "team_lead") {
      fetchPasswords();
    } else {
      fetchPasswords();
    }
    
    // Show success message
    setError(`Your role has been updated to ${updatedUser.role}. Dashboard updated.`);
    setTimeout(() => setError(''), 3000);
  }
}, [currentUser, fetchAllPasswords, fetchPasswords]);


  const handleLogout = () => {
    setCurrentUser(null);
    sessionStorage.removeItem("currentUser"); // ✅ clears only this tab’s session
    setPasswords([]);
    setError('');
  };

  const renderContent = () => {
  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} users={users} error={error} setError={setError} isLoading={isLoading} setIsLoading={setIsLoading} />;
  } else if (currentUser.role === 'admin') {
    return (
    <AdminDashboard
      currentUser={currentUser}
      passwords={passwords}
      personalPasswords={personalPasswords}
      users={users}
      teamLeads={teamLeads}
      onLogout={handleLogout}
      isLoading={isLoading}
      fetchPasswords={fetchAllPasswords} 
      fetchUsersAndTeams={fetchUsersAndTeams}
      apiLoading={apiLoading}
      setLoading={setLoading}
      fetchAllPasswords={fetchAllPasswords} // Add this line
      setLastUpdateCheck={setLastUpdateCheck} // Add this line
    />
  );
  } else if (currentUser.role === 'team_lead') {
    return (
      <TeamLeadDashboard
        currentUser={currentUser}
        passwords={passwords}
        personalPasswords={personalPasswords}
        users={users}
        teamLeads={teamLeads}
        onLogout={handleLogout}
        isLoading={isLoading}
        fetchPasswords={fetchPasswords}
        fetchUsersAndTeams={fetchUsersAndTeams}
        apiLoading={apiLoading}
        setLoading={setLoading}
        setLastUpdateCheck={setLastUpdateCheck} // Add this line for consistency
      />
    );
  } else {
    return (
      <MemberDashboard
        currentUser={currentUser}
        passwords={passwords}
        personalPasswords={personalPasswords}
        onLogout={handleLogout}
        isLoading={isLoading}
        fetchPasswords={fetchPasswords}
        apiLoading={apiLoading}
        setLoading={setLoading}
        setLastUpdateCheck={setLastUpdateCheck}
        lastUpdateCheck={lastUpdateCheck}
      />
    );
  }
};

   return (
    <>
      {showIntro && <LogoAnimation onAnimationComplete={handleAnimationComplete} />}
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 font-sans antialiased text-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl bg-white/80 backdrop-blur-md rounded-2xl shadow-xl p-6 lg:p-8 transition-all duration-300 border border-white/30">
          {renderContent()}
        </div>
      </div>
    </>
  );
}

// Login Page Component
function LoginPage({ onLogin, users, error, setError, isLoading, setIsLoading }) {
  const [microsoftId, setMicrosoftId] = useState('');
  const [password, setPassword] = useState('');

  // Check if we're in the middle of Microsoft auth on component mount
  useEffect(() => {
    const authLoading = localStorage.getItem('microsoft_auth_loading');
    if (authLoading === 'true') {
      setIsLoading(true);
    }
  }, [setIsLoading]);

  const handleLoginAttempt = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'login',
        microsoftId: microsoftId,
        password: password
      });
      if (response.data.success) {
        onLogin(response.data.user);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError("An error occurred during login. Please try again.");
      console.error("Login API Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
  setIsLoading(true);
  
  const clientId = process.env.REACT_APP_MS_CLIENT_ID || "cd8ed851-3ac2-484b-bb64-70d696de6cd6";
  const redirectUri = process.env.REACT_APP_MS_REDIRECT_URI || "https://password.bitaccounting.com/auth-callback";
  const tenantId = process.env.REACT_APP_MS_TENANT_ID || "a64706e1-b89d-4818-8328-00ba8c01eb58";
  
  // Generate a unique state parameter for CSRF protection
  const state = Math.random().toString(36).substring(2, 15) + 
                Math.random().toString(36).substring(2, 15);
  
  // Store state for verification after redirect
  localStorage.setItem('microsoft_auth_state', state);
  
  const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
    `client_id=${clientId}` +
    `&response_type=code` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&response_mode=query` +
    `&scope=openid%20profile%20email%20User.Read` +
    `&state=${state}`;
  
  // Redirect to Microsoft login
  window.location.href = authUrl;
};

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-6">
        <div className="h-16 w-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Key className="text-white" size={32} />
        </div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">Password Vault</h1>
        <p className="text-gray-500 text-sm">Secure credential management system</p>
      </div>
      
      <div className="w-full max-w-xs space-y-4">
        <form onSubmit={handleLoginAttempt} className="space-y-4">
          <div className="relative">
            <input
              type="email"
              value={microsoftId}
              onChange={(e) => setMicrosoftId(e.target.value)}
              placeholder="Microsoft ID"
              className="w-full p-3 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              required
            />
            <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
          
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full p-3 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              required
            />
            <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
          
          {error && <p className="text-red-500 text-sm animate-pulse">{error}</p>}
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-3 px-4 rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none flex items-center justify-center"
          >
            {isLoading ? (
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <LogIn className="mr-2" size={18} /> Sign In
              </>
            )}
          </button>
        </form>
        
        <div className="relative flex items-center py-2">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-4 text-gray-400 text-xs">Or continue with</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        
        <button
          onClick={handleMicrosoftLogin}
          disabled={isLoading}
          className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-lg shadow-sm border border-gray-300 transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none flex items-center justify-center"
        >
          {isLoading ? (
            <div className="h-5 w-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Microsoft Logo" className="w-5 h-5 mr-2" />
              Microsoft Account
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Admin Dashboard Component
function AdminDashboard({ currentUser, passwords, personalPasswords, users, teamLeads, fetchUsersAndTeams, onLogout, isLoading, fetchPasswords, apiLoading, setLoading,fetchAllPasswords,setLastUpdateCheck,handleRoleChange}) {
  const [activeTab, setActiveTab] = useState('passwords');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPersonal, setShowPersonal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

 useEffect(() => {
  if (currentUser && currentUser.role === 'admin') {
    fetchAllPasswords();
    
    // Set up a one-time initial load flag to prevent update system interference
    const initialLoadTimer = setTimeout(() => {
      setLastUpdateCheck(new Date().toISOString());
    }, 2000);
    
    return () => clearTimeout(initialLoadTimer);
  }
}, [currentUser, fetchAllPasswords, setLastUpdateCheck]); // Add dependencies

  const allPasswords = showPersonal ? [...passwords, ...personalPasswords] : passwords;
  
  const filteredPasswords = allPasswords.filter(p =>
  p && (
    (p.business_name && p.business_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.title && p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.username && p.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.user_name && p.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.login_page_url && p.login_page_url.toLowerCase().includes(searchTerm.toLowerCase()))
  )
);

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between w-full sm:w-auto">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-2 rounded-lg">
              <Briefcase className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome back, {currentUser.name}</p>
            </div>
          </div>
          <button 
            className="sm:hidden text-gray-500"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
        </div>
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} sm:block mt-4 sm:mt-0`}>
          <button
            onClick={onLogout}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors w-full sm:w-auto justify-center sm:justify-start py-2 sm:py-0"
          >
            <LogOut size={16} className="mr-1" /> Logout
          </button>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('passwords')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${activeTab === 'passwords' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Key className="inline-block mr-2" size={16} /> Passwords
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${activeTab === 'users' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Users className="inline-block mr-2" size={16} /> User Management
        </button>
      </div>
      
      {activeTab === 'passwords' && (
      <PasswordManagement
        currentUser={currentUser}
        passwords={passwords}
        personalPasswords={personalPasswords}
        filteredPasswords={filteredPasswords}
        users={users}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showPersonal={showPersonal}
        setShowPersonal={setShowPersonal}
        fetchPasswords={fetchPasswords}
        apiLoading={apiLoading}
        setLoading={setLoading}
      />
    )}
    
    {activeTab === 'users' && (
      <UserManagement
        users={users}
        teamLeads={teamLeads}
        fetchUsersAndTeams={fetchUsersAndTeams}
        currentUser={currentUser}
        apiLoading={apiLoading}
        setLoading={setLoading}
        onRoleChange={handleRoleChange} // Make sure this is passed
      />
    )}
    </div>
  );
}

// Team Lead Dashboard Component - FIXED VERSION
function TeamLeadDashboard({ currentUser, passwords, personalPasswords, users, teamLeads, fetchPasswords, fetchUsersAndTeams, onLogout, isLoading, apiLoading, setLoading, setLastUpdateCheck }) {
  const [activeTab, setActiveTab] = useState('passwords');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPersonal, setShowPersonal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeam, setShowTeam] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lastUpdateCheck, setLocalLastUpdateCheck] = useState(new Date().toISOString());

  // Function to fetch team members
const fetchTeamMembers = useCallback(async () => {
  // Add a simple throttle - don't fetch if already loading
  if (apiLoading.getUsers) return;
  
  setLoading('getUsers', true);
  try {
    const response = await axios.post(API_URL, {
      action: 'get_team_members',
      teamLeadId: currentUser.id
    });
    if (response.data.success) {
      setTeamMembers(response.data.team_members);
    }
  } catch (error) {
    console.error("Error fetching team members:", error);
  } finally {
    setLoading('getUsers', false);
  }
}, [currentUser.id, apiLoading.getUsers]); // Only depend on currentUser.id and apiLoading.getUsers

// Fetch team members on component mount - FIXED VERSION
useEffect(() => {
  if (currentUser) {
    fetchTeamMembers();
  }
}, [currentUser?.id]);

  // Use useRef for stable function reference
  const checkForUpdatesRef = useRef(null);

  // Initialize the checkForUpdates function
  useEffect(() => {
    checkForUpdatesRef.current = async () => {
      if (!currentUser) return;

      try {
        const response = await axios.post(API_URL, {
          action: 'check_updates',
          lastCheck: lastUpdateCheck,
          userId: currentUser.id,
          userRole: currentUser.role,
          teamLeadId: currentUser.id
        });

        if (response.data && response.data.success) {
          const hasUserUpdates = response.data.updates && response.data.updates.some(update => 
            update.table_name === 'users' || 
            update.table_name === 'password_assignments' || 
            update.table_name === 'personal_password_assignments'
          );

          const hasPasswordUpdates = response.data.updates && response.data.updates.some(update => 
            update.table_name === 'passwords' || 
            update.table_name === 'personal_passwords'
          );

          if (hasUserUpdates) {
            await fetchTeamMembers();
            await fetchUsersAndTeams();
          }

          if (hasPasswordUpdates && response.data.updates.length > 0) {
            await fetchPasswords();
          }

          // Update timestamp only if we actually processed updates
          if (hasUserUpdates || hasPasswordUpdates) {
            setLocalLastUpdateCheck(response.data.currentTime || new Date().toISOString());
            setLastUpdateCheck(response.data.currentTime || new Date().toISOString());
          }
        }
      } catch (error) {
        console.error("Team Lead: Error checking for updates:", error);
      }
    };
  }, [currentUser, fetchTeamMembers, fetchPasswords, fetchUsersAndTeams, lastUpdateCheck, setLastUpdateCheck]);

  // Set up interval for update checking
  useEffect(() => {
    if (!currentUser) return;

    let interval;

    const setupInterval = () => {
      if (interval) {
        clearInterval(interval);
      }
      
      interval = setInterval(() => {
        if (checkForUpdatesRef.current) {
          checkForUpdatesRef.current();
        }
      }, UPDATE_CHECK_INTERVAL);
    };

    // Initial setup
    setupInterval();

    // Set up visibility change listener
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(interval);
        interval = setInterval(() => {
          if (checkForUpdatesRef.current) {
            checkForUpdatesRef.current();
          }
        }, BACKGROUND_UPDATE_CHECK_INTERVAL);
      } else {
        clearInterval(interval);
        setupInterval();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Fetch team members on component mount
  useEffect(() => {
    if (currentUser) {
      fetchTeamMembers();
      
      // Set initial update check timestamp after a short delay
      const timer = setTimeout(() => {
        setLocalLastUpdateCheck(new Date().toISOString());
        setLastUpdateCheck(new Date().toISOString());
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [currentUser, fetchTeamMembers, setLastUpdateCheck]);

  const allPasswords = showPersonal ? [...passwords, ...personalPasswords] : passwords;

  const filteredPasswords = allPasswords.filter(p =>
    p && (
      (p.business_name && p.business_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.title && p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.username && p.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.user_name && p.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.login_page_url && p.login_page_url.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between w-full sm:w-auto">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-2 rounded-lg">
              <Users className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Team Lead Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome, {currentUser.name}</p>
            </div>
          </div>
          <button 
            className="sm:hidden text-gray-500"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
        </div>
        <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} sm:block mt-4 sm:mt-0`}>
          <button
            onClick={onLogout}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors w-full sm:w-auto justify-center sm:justify-start py-2 sm:py-0"
          >
            <LogOut size={16} className="mr-1" /> Logout
          </button>
        </div>
      </div>
      
      <div className="mb-4">
        <button 
          onClick={() => setShowTeam(!showTeam)}
          className="flex items-center text-sm font-medium text-gray-700 mb-2"
        >
          {showTeam ? <ChevronUp size={16} className="mr-1" /> : <ChevronDown size={16} className="mr-1" />}
          Your Team ({teamMembers.length} members)
        </button>
        
        {showTeam && (
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
            <div className="flex flex-wrap gap-2">
              {teamMembers.length > 0 ? (
                teamMembers.map(member => (
                  <span key={member.id} className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs font-medium shadow-sm">
                    {member.name}
                  </span>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No team members assigned yet.</p>
              )}
            </div>
          </div>
        )}
      </div>
      
      {activeTab === 'passwords' && (
        <PasswordManagement
          currentUser={currentUser}
          passwords={passwords}
          personalPasswords={personalPasswords}
          filteredPasswords={filteredPasswords}
          users={teamMembers}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showPersonal={showPersonal}
          setShowPersonal={setShowPersonal}
          fetchPasswords={fetchPasswords}
          isTeamLead={true}
          showOwnerInfo={true}
          apiLoading={apiLoading}
          setLoading={setLoading}
        />
      )}
    </div>
  );
}

function UserManagement({ users, teamLeads, fetchUsersAndTeams, currentUser, apiLoading, setLoading,onRoleChange }) {
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editUserRole, setEditUserRole] = useState('member');
  const [editUserTeamLead, setEditUserTeamLead] = useState('');
  
  // Add effect to log when users prop changes (for debugging)
  useEffect(() => {
    console.log('UserManagement: Users list updated', users.length, 'users');
  }, [users]);

  // Update team lead dropdown when role changes
  useEffect(() => {
    if (editingUser) {
      if (editUserRole === 'admin' || editUserRole === 'team_lead') {
        setEditUserTeamLead(''); // Clear team lead selection for admin/team_lead roles
      }
    }
  }, [editUserRole, editingUser]);

  const handleDeleteUser = async (userId) => {
    if (userId === 1) {
      setError("Cannot delete the primary admin user.");
      return;
    }
    const isConfirmed = window.confirm("Are you sure you want to delete this user?");
    if (isConfirmed) {
      setLoading('deleteUser', true);
      try {
        const response = await axios.post(API_URL, { 
          action: 'delete_user', 
          id: userId
        });
        if (response.data.success) {
          // Users list will be automatically updated via the update checking system
          setError('');
          // Show success message
          setError('User deleted successfully');
          setTimeout(() => setError(''), 3000);
        } else {
          setError(response.data.message);
        }
      } catch (error) {
        setError("Error deleting user");
        console.error("Error deleting user:", error);
      } finally {
        setLoading('deleteUser', false);
      }
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserRole(user.role);
    setEditUserTeamLead(user.team_lead_id || '');
  };

  const handleUpdateUser = async () => {
  if (!editingUser || !currentUser || !currentUser.id) {
    setError("User session expired. Please log in again.");
    return;
  }
  
  setLoading('updateUser', true);

  try {
    const response = await axios.post(API_URL, {
      action: 'update_user',
      id: editingUser.id,
      role: editUserRole,
      team_lead_id: (editUserRole === 'member' && editUserTeamLead) ? editUserTeamLead : null,
      requestingUserId: currentUser.id
    });
    
    if (response.data.success) {
      setEditingUser(null);
      
      // ✅ Handle role changes for current user
      const updatedUser = response.data.updatedUser;
      const savedUser = JSON.parse(sessionStorage.getItem("currentUser"));
      
      if (updatedUser && savedUser && updatedUser.id === savedUser.id) {
        // Update session and state
        const updatedCurrentUser = {
          ...savedUser,
          role: updatedUser.role,
          team_lead_id: updatedUser.team_lead_id,
          team_lead_name: updatedUser.team_lead_name
        };
        
        sessionStorage.setItem("currentUser", JSON.stringify(updatedCurrentUser));
        
        // Use callback if provided, otherwise reload
        if (onRoleChange) {
          onRoleChange(updatedCurrentUser);
          setError(`Your role has been updated to ${updatedUser.role}. Dashboard updated.`);
        } else {
          setError('Your role has been updated. Page will reload to apply changes.');
          setTimeout(() => window.location.reload(), 2000);
        }
      } else {
        // Regular user update
        await fetchUsersAndTeams();
        setError('User updated successfully');
      }
      
      setTimeout(() => setError(''), 3000);
    } else {
      setError(response.data.message);
    }
  } catch (error) {
    setError("Error updating user: " + (error.response?.data?.message || error.message));
  } finally {
    setLoading('updateUser', false);
  }
};

  const cancelEdit = () => {
    setEditingUser(null);
    setError('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">User Management</h3>
          <div className="text-sm text-gray-500">
            {users.length} user{users.length !== 1 ? 's' : ''} total
          </div>
        </div>
        
        {error && (
          <div className={`mb-4 p-3 rounded-lg ${
            error.includes('successfully') 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {error}
          </div>
        )}
        
        {/* Mobile View - Cards */}
        <div className="block md:hidden space-y-4">
          {users.map(user => (
            <div key={user.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-medium text-gray-800">{user.name}</h4>
                  <p className="text-sm text-gray-600">{user.microsoft_id}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  user.role === 'admin' ? 'bg-red-100 text-red-800' : 
                  user.role === 'team_lead' ? 'bg-blue-100 text-blue-800' : 
                  'bg-gray-100 text-gray-800'
                }`}>
                  {user.role}
                </span>
              </div>
              
              <div className="text-sm text-gray-600 mb-3">
                <span className="font-medium">Team Lead:</span> {user.team_lead_name || 'None'}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => handleEditUser(user)}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteUser(user.id)}
                  className="text-red-600 hover:text-red-900 text-sm font-medium transition-colors"
                  disabled={user.id === 1}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Desktop View - Table */}
        <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Lead</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{user.microsoft_id}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                      user.role === 'admin' ? 'bg-red-100 text-red-800' : 
                      user.role === 'team_lead' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{user.team_lead_name || 'None'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      disabled={user.id === 1}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md mx-auto my-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Edit User: {editingUser.name}</h3>
                <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editUserRole}
                    onChange={(e) => setEditUserRole(e.target.value)}
                    className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="member">Member</option>
                    <option value="team_lead">Team Lead</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                {/* Show Team Lead dropdown only for member role */}
                {editUserRole === 'member' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Team Lead</label>
                    <select
                        value={editUserTeamLead}
                        onChange={(e) => setEditUserTeamLead(e.target.value)}
                        className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                        <option value="">Select Team Lead (Optional)</option>
                        {teamLeads
                            .filter(lead => lead.id !== editingUser.id) // Prevent self-assignment
                            .map(lead => (
                                <option key={lead.id} value={lead.id}>
                                    {lead.name}
                                </option>
                            ))
                        }
                    </select>
                </div>
            )}
              </div>
              
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 mt-6">
              <button 
                onClick={cancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
              >
                Cancel
              </button>
              
              <LoadingButton
                onClick={handleUpdateUser}
                isLoading={apiLoading.updateUser}
                loadingText="Updating..."
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors order-1 sm:order-2"
              >
                Save Changes
              </LoadingButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Password Card Component - Keep "Personal (John)" but remove "Assigned to: John"
function PasswordCard({ password, isAdmin, isTeamLead, showOwnerInfo, onEdit, onDelete, isPersonal, onAssignUsers, allUsers }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedUsername, setCopiedUsername] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  
  const handleCopy = (text, type) => {
    copyToClipboard(text, type);
    if (type === 'Username') {
      setCopiedUsername(true);
      setTimeout(() => setCopiedUsername(false), 2000);
    } else {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const handleAssignedUsersClick = () => {
    if ((isAdmin || isTeamLead) && !isPersonal && password.assigned_users && password.assigned_users.length > 0) {
      onAssignUsers && onAssignUsers(password, isPersonal);
    }
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
      <div className="p-4 flex-grow">
        <div className="flex justify-between items-start mb-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-800 text-sm truncate" title={password.business_name}>
              {password.business_name}
            </h3>
            <p className="text-xs text-gray-500 truncate" title={password.title}>
              {password.title}
            </p>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0 ml-2">            
            {/* Show assign button only for admins/team leads on assigned passwords */}
            {(isAdmin || isTeamLead) && !isPersonal && onAssignUsers && (
              <button 
                onClick={() => onAssignUsers(password, isPersonal)} 
                className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                title="Assign to users"
              >
                <UserPlus size={14} />
              </button>
            )}
            
            {/* Show edit/delete buttons for:
                1. Admins on all passwords
                2. Team leads on team passwords (not personal)
                3. All users on their personal passwords */}
            {(isAdmin || 
              (isTeamLead && !isPersonal) || 
              isPersonal) && (
              <>
                {onEdit && (
                  <button 
                    onClick={() => onEdit(password, isPersonal)} 
                    className="text-indigo-600 hover:text-indigo-800 transition-colors p-1"
                    title="Edit password"
                  >
                    <Edit size={14} />
                  </button>
                )}
                {onDelete && (
                  <button 
                    onClick={() => onDelete(password.id, isPersonal)} 
                    className="text-red-600 hover:text-red-800 transition-colors p-1"
                    title="Delete password"
                  >
                    <Trash size={14} />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center">
            <Link className="text-gray-400 mr-1 flex-shrink-0" size={14} />
            <a 
              href={password.login_page_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-indigo-600 hover:text-indigo-800 truncate block flex-1"
              title={password.login_page_url}
            >
              {password.login_page_url.replace(/^https?:\/\//, '')}
            </a>
          </div>
          
          <div className="flex items-center">
            <UserPlus className="text-gray-400 mr-1 flex-shrink-0" size={14} />
            <span className="text-xs text-gray-700 truncate flex-1" title={password.username}>
              {password.username}
            </span>
            <button 
              onClick={() => handleCopy(password.username, 'Username')} 
              className="ml-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title="Copy username"
            >
              {copiedUsername ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
            </button>
          </div>
          
          <div className="flex items-center">
            <Key className="text-gray-400 mr-1 flex-shrink-0" size={14} />
            <span className="text-xs font-mono truncate flex-1" title={showPassword ? password.password : 'Password'}>
              {showPassword ? password.password : '••••••••'}
            </span>
            <button 
              onClick={() => setShowPassword(!showPassword)} 
              className="ml-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
            <button 
              onClick={() => handleCopy(password.password, 'Password')} 
              className="ml-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              title="Copy password"
            >
              {copiedPassword ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
            </button>
          </div>
          
          {/* Show personal owner info for admins and team leads */}
          {(showOwnerInfo || isAdmin) && password.user_name && password.password_type === 'personal' && (
            <div className="flex items-center">
              <Users className="text-gray-400 mr-1 flex-shrink-0" size={14} />
              <span className="text-xs text-gray-700 truncate flex-1" title={`Owner: ${password.user_name}`}>
                
                {password.user_name}
              </span>
            </div>
          )}
          
          {/* Show assigned users count for admins and team leads */}
          {(isAdmin || isTeamLead) && password.assigned_users && password.assigned_users.length > 0 && password.password_type !== 'personal' && (
            <div 
              onClick={handleAssignedUsersClick}
              className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
              title="Click to manage assigned users"
            >
              <Users className="text-gray-400 mr-1 flex-shrink-0" size={14} />
              <span className="text-xs text-gray-700">
                
                {password.assigned_users.length} user{password.assigned_users.length !== 1 ? 's' : ''} assigned
              </span>
              
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignUsersModal({ password, isPersonal, users, onClose, onSave, apiLoading, setLoading}) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(''); // Add local error state

  useEffect(() => {
    // Pre-select currently assigned users
    if (password.assigned_users) {
      setSelectedUsers(password.assigned_users.map(user => user.id));
    }
  }, [password]);

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

const handleSave = async () => {
  setError(''); // Clear any previous errors
  setLoading('assignUsers', true);
  
  try {
    // Call the save function and wait for completion
    const result = await onSave(password.id, selectedUsers, isPersonal);
    
    if (result && result.success) {
      // Close modal on success - the parent component will handle the refresh
      onClose();
    } else {
      // Show error message if save failed
      setError(result?.message || "Failed to save assignments");
    }
  } catch (error) {
    // Handle network errors or other exceptions
    console.error("Error in handleSave:", error);
    setError("Network error occurred. Please check your connection and try again.");
  } finally {
    setLoading('assignUsers', false);
  }
};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md mx-auto my-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Assign "{password.business_name}" to Users
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        
        {/* Display error message if exists */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
        
        <div className="max-h-96 overflow-y-auto mb-4">
          <div className="space-y-2">
            {users.map(user => (
              <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => handleUserToggle(user.id)}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-3 text-sm text-gray-700">{user.name}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
            disabled={apiLoading.assignUsers}
          >
            Cancel
          </button>
          
          <LoadingButton
            onClick={handleSave}
            isLoading={apiLoading.assignUsers}
            loadingText="Saving..."
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center order-1 sm:order-2"
          >
            Save Assignments
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

// Password Management Component with grid layout
function PasswordManagement({ currentUser, passwords, personalPasswords, filteredPasswords, users, searchTerm, setSearchTerm, showPersonal, setShowPersonal, fetchPasswords, isTeamLead = false, showOwnerInfo = false, apiLoading, setLoading  }) {
  const [formState, setFormState] = useState({
    id: null,
    business_name: '',
    title: '',
    login_page_url: '',
    username: '',
    password: '',
    user_ids: [],
    isPersonal: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [assignUsersModal, setAssignUsersModal] = useState(null);
  const [showAssigned, setShowAssigned] = useState(true);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [isUsersDropdownOpen, setIsUsersDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Separate assigned passwords from personal passwords
  const assignedPasswords = filteredPasswords.filter(p => p.password_type === 'assigned');
  const personalPasswordsFiltered = filteredPasswords.filter(p => p.password_type === 'personal');
  
  
  // Filter out unassigned passwords for non-admin users
  const displayAssignedPasswords = showAssigned 
    ? assignedPasswords.filter(p => {
        // Admin sees all passwords (assigned and unassigned)
        if (currentUser.role === 'admin') return true;
        
        // Team leads see passwords that have assigned users OR passwords they created
        if (currentUser.role === 'team_lead') {
          return (p.assigned_users && p.assigned_users.length > 0) || p.user_id === currentUser.id;
        }
        
        // ✅ FIXED: Members only see passwords that are assigned to them
        if (currentUser.role === 'member') {
          return p.assigned_users && p.assigned_users.some(u => u.id === currentUser.id);
        }
        
        return true;
      })
    : assignedPasswords.filter(p => !p.assigned_users || p.assigned_users.length === 0);

// Personal passwords are always shown to their owners and admins
const displayPersonalPasswords = personalPasswordsFiltered.filter(p => {
  if (currentUser.role === 'admin') return true;
  return p.user_id === currentUser.id;
});

// Combine both types for display
const displayPasswords = [...displayAssignedPasswords, ...displayPersonalPasswords].map((password, index) => ({
  ...password,
  uniqueKey: `${password.password_type}_${password.id}_${index}`
}));

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.microsoft_id.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // Get selected user names for display
  const selectedUserNames = formState.user_ids
    .map(id => users.find(user => user.id === id)?.name)
    .filter(Boolean);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleAddOrUpdatePassword = async (e) => {
  e.preventDefault();
  setLoading(isEditing ? 'updatePassword' : 'addPassword', true);
  
  try {
    const action = formState.isPersonal ? 
      (isEditing ? 'update_personal_password' : 'add_personal_password') : 
      (isEditing ? 'update_password' : 'add_password');
    
    const payload = {
      action: action,
      id: formState.id,
      businessName: formState.business_name,
      title: formState.title,
      loginPageUrl: formState.login_page_url,
      username: formState.username,
      password: formState.password,
    };

    // For personal passwords, include the user ID
    if (formState.isPersonal) {
      payload.userId = currentUser.id;
    } 
    // For assigned passwords, include the user IDs array
    else {
      payload.userIds = formState.user_ids;
    }
    
    const response = await axios.post(API_URL, payload);
    
    if (response.data && response.data.success) {
      await fetchPasswords(); // Wait for refresh
      setIsEditing(false);
      setFormState({
        id: null, business_name: '', title: '', login_page_url: '', username: '', password: '',
        user_ids: [], isPersonal: false
      });
      setError('');
      setShowForm(false);
      setIsUsersDropdownOpen(false);
      setUserSearchTerm('');
    } else {
      setError(response.data?.message || "Operation failed");
    }
  } catch (error) {
    setError("Error saving password: " + (error.response?.data?.message || error.message));
    console.error("Error saving password:", error);
  } finally {
    setLoading(isEditing ? 'updatePassword' : 'addPassword', false);
  }
};

  const handleEditClick = (password, isPersonal = false) => {
    setFormState({
        id: password.id,
        business_name: password.business_name,
        title: password.title,
        login_page_url: password.login_page_url,
        username: password.username,
        password: password.password,
        user_ids: password.assigned_users ? password.assigned_users.map(u => u.id) : [],
        isPersonal: isPersonal
    });
    setIsEditing(true);
    setShowForm(true);
    setError('');
  };

  const handleDeletePassword = async (passwordId, isPersonal = false) => {
  const isConfirmed = window.confirm("Are you sure you want to delete this password?");
  if (isConfirmed) {
    setLoading('deletePassword', true);
    try {
      const response = await axios.post(API_URL, { 
        action: 'delete_password', 
        id: passwordId,
        isPersonal: isPersonal
      });
      
      if (response.data && response.data.success) {
        await fetchPasswords(); // Wait for refresh
        setError('');
      } else {
        setError(response.data?.message || "Delete failed");
      }
    } catch (error) {
      setError("Error deleting password: " + (error.response?.data?.message || error.message));
      console.error("Error deleting password:", error);
    } finally {
      setLoading('deletePassword', false);
    }
  }
};

  const cancelEdit = () => {
    setIsEditing(false);
    setFormState({
      id: null, business_name: '', title: '', login_page_url: '', username: '', password: '',
      user_ids: [], isPersonal: false
    });
    setError('');
    setShowForm(false);
    setIsUsersDropdownOpen(false);
    setUserSearchTerm('');
  };
  
  const handleAssignUsers = (password, isPersonal) => {
    setAssignUsersModal({ password, isPersonal });
  };

const handleSaveUserAssignments = async (passwordId, userIds, isPersonal) => {
  setLoading('assignUsers', true);
  try {
    const response = await axios.post(API_URL, {
      action: 'assign_password_to_users',
      passwordId: passwordId,
      userIds: userIds,
      isPersonal: isPersonal
    });
    
    if (response.data.success) {
      // ✅ CRITICAL: Immediately update local state to reflect changes
      await fetchPasswords(); // This will refresh the passwords list
      
      // ✅ FORCE IMMEDIATE UI UPDATE by closing the modal and updating state
      setAssignUsersModal(null);
      setError('');
      
      return response.data;
    } else {
      setError(response.data.message || "Failed to assign users");
      return response.data;
    }
  } catch (error) {
    console.error("Error assigning users:", error);
    setError("Error assigning users to password");
    return { success: false, message: "Network error occurred" };
  } finally {
    setLoading('assignUsers', false);
  }
};

  const selectAllUsers = () => {
  setFormState(prev => ({
    ...prev,
    user_ids: filteredUsers.map(user => user.id)
  }));
};

const clearAllUsers = () => {
  setFormState(prev => ({
    ...prev,
    user_ids: []
  }));
};

const toggleUserSelection = (userId) => {
  setFormState(prev => ({
    ...prev,
    user_ids: prev.user_ids.includes(userId)
      ? prev.user_ids.filter(id => id !== userId)
      : [...prev.user_ids, userId]
  }));
};

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-gray-800">Password Management</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          {/* Hide Assigned Button */}
          <button
            onClick={() => setShowAssigned(!showAssigned)}
            className={`px-4 py-2 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center ${
              showAssigned
                ? 'bg-gradient-to-r from-green-600 to-teal-700 text-white'
                : 'bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white'
            }`}
          >
            <Users className="mr-2" size={16} />
            {showAssigned ? 'Hide Assigned' : 'Show Assigned'}
          </button>
          
          {/* Show Personal Button */}
          <button
            onClick={() => setShowPersonal(!showPersonal)}
            className={`px-4 py-2 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center ${
              showPersonal
                ? 'bg-gradient-to-r from-blue-600 to-cyan-700 text-white'
                : 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white'
            }`}
          >
            <Lock className="mr-2" size={16} />
            {showPersonal ? 'Hide Personal' : 'Show Personal'}
          </button>
          
          {/* Search Input */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search passwords..." 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
            />
          </div>
        </div>
      </div>
      
      {!showForm ? (
        <div className="flex flex-col sm:flex-row gap-4">
          <LoadingButton
            onClick={() => {
              setFormState({
                id: null, business_name: '', title: '', login_page_url: '', username: '', password: '',
                user_ids: [], isPersonal: false
              });
              setShowForm(true);
              setIsEditing(false);
            }}
            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium py-3 px-4 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
          >
            <Plus className="mr-2" size={20} /> Assign New Password
          </LoadingButton>
          
          <LoadingButton
            onClick={() => {
              setFormState({
                id: null, business_name: '', title: '', login_page_url: '', username: '', password: '',
                user_ids: [currentUser.id], isPersonal: true
              });
              setShowForm(true);
              setIsEditing(false);
            }}
            className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-medium py-3 px-4 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
          >
            <Lock className="mr-2" size={20} /> Add Personal Password
          </LoadingButton>
        </div>
      ) : (
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {isEditing ? 'Edit Password' : formState.isPersonal ? 'Add Personal Password' : 'Assign New Password'}
          </h3>
          
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          
          <form onSubmit={handleAddOrUpdatePassword} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                <input 
                  type="text" 
                  name="business_name" 
                  value={formState.business_name} 
                  onChange={handleInputChange} 
                  required 
                  className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title/Description</label>
                <input 
                  type="text" 
                  name="title" 
                  value={formState.title} 
                  onChange={handleInputChange} 
                  required 
                  className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Login Page URL</label>
              <input 
                type="url" 
                name="login_page_url" 
                value={formState.login_page_url} 
                onChange={handleInputChange} 
                required 
                className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input 
                  type="text" 
                  name="username" 
                  value={formState.username} 
                  onChange={handleInputChange} 
                  required 
                  className="w-full p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input 
                    type={showPasswordInput ? "text" : "password"}
                    name="password" 
                    value={formState.password} 
                    onChange={handleInputChange} 
                    required 
                    className="w-full p-2.5 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordInput(!showPasswordInput)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex="-1"
                  >
                    {showPasswordInput ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Show user dropdown checkboxes for admins/team leads when creating an assigned password */}
            {(currentUser.role === 'admin' || currentUser.role === 'team_lead') && !formState.isPersonal && !isEditing && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign to Users</label>
              
              {/* Dropdown trigger with improved styling */}
              <div 
                className="w-full p-3 border border-gray-300 rounded-lg cursor-pointer bg-white hover:border-indigo-400 transition-all duration-200 flex items-center justify-between shadow-sm"
                onClick={() => setIsUsersDropdownOpen(!isUsersDropdownOpen)}
              >
                <div className="flex flex-wrap gap-1.5 flex-1 min-h-[28px]">
                  {selectedUserNames.length > 0 ? (
                    selectedUserNames.map((name, index) => (
                      <span 
                        key={index} 
                        className="bg-indigo-100 text-indigo-700 px-2.5 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border border-indigo-200"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const user = users.find(u => u.name === name);
                            if (user) {
                              setFormState(prev => ({
                                ...prev,
                                user_ids: prev.user_ids.filter(id => id !== user.id)
                              }));
                            }
                          }}
                          className="text-indigo-500 hover:text-indigo-700"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 italic">Click to select users...</span>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {formState.user_ids.length > 0 && (
                    <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full">
                      {formState.user_ids.length} selected
                    </span>
                  )}
                  {isUsersDropdownOpen ? (
                    <ChevronUp size={18} className="text-gray-500" />
                  ) : (
                    <ChevronDown size={18} className="text-gray-500" />
                  )}
                </div>
              </div>
              
              {/* Enhanced Dropdown content */}
              {isUsersDropdownOpen && (
                <div className="absolute z-20 w-full mt-1.5 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
                  {/* Header with search and actions */}
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                      {userSearchTerm && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserSearchTerm('');
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAllUsers();
                        }}
                        className="flex-1 py-2 px-3 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors flex items-center justify-center gap-1"
                      >
                        <Check size={12} />
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAllUsers();
                        }}
                        className="flex-1 py-2 px-3 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors flex items-center justify-center gap-1"
                      >
                        <X size={12} />
                        Clear All
                      </button>
                    </div>
                  </div>
                  
                  {/* Users list with better styling */}
                  <div className="max-h-56 overflow-y-auto">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(user => (
                        <label
                          key={user.id}
                          className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 group transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={`flex items-center justify-center w-5 h-5 border-2 rounded ${
                            formState.user_ids.includes(user.id)
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'border-gray-300 group-hover:border-indigo-400'
                          } transition-colors`}>
                            {formState.user_ids.includes(user.id) && (
                              <Check size={12} strokeWidth={3} />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={formState.user_ids.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="absolute opacity-0"
                          />
                          <div className="ml-3 flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.microsoft_id}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ml-2 ${
                            user.role === 'admin' 
                              ? 'bg-red-100 text-red-800' 
                              : user.role === 'team_lead'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.role}
                          </span>
                        </label>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        <Users size={20} className="mx-auto mb-2 text-gray-300" />
                        No users found matching "{userSearchTerm}"
                      </div>
                    )}
                  </div>
                  
                  {/* Footer with selection info */}
                  <div className="p-3 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">{formState.user_ids.length}</span> user{formState.user_ids.length !== 1 ? 's' : ''} selected
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsUsersDropdownOpen(false)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            
            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button 
              type="button" 
              onClick={cancelEdit}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors order-2 sm:order-1"
            >
              Cancel
            </button>
            
            <LoadingButton
              type="submit"
              isLoading={apiLoading.addPassword || apiLoading.updatePassword}
              loadingText={isEditing ? "Updating..." : "Saving..."}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors order-1 sm:order-2"
            >
              <Save className="mr-2" size={16} /> {isEditing ? 'Update' : 'Save'} Password
            </LoadingButton>
          </div>
          </form>
        </div>
      )}
      
      {displayPasswords.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayPasswords.map(password => (
            <PasswordCard 
              key={password.uniqueKey}  // Use the unique key instead of just id
              password={password} 
              isAdmin={currentUser.role === 'admin'}
              isTeamLead={currentUser.role === 'team_lead'}
              showOwnerInfo={showOwnerInfo}
              onEdit={handleEditClick}
              onDelete={handleDeletePassword}
              isPersonal={password.password_type === 'personal'}
              onAssignUsers={handleAssignUsers}
              allUsers={users}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Key className="text-gray-400" size={24} />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-1">No passwords found</h3>
          <p className="text-gray-500 text-sm">
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first password'}
          </p>
        </div>
      )}
      
      {assignUsersModal && (
        <AssignUsersModal
          password={assignUsersModal.password}
          isPersonal={assignUsersModal.isPersonal}
          users={users}
          onClose={() => setAssignUsersModal(null)}
          onSave={handleSaveUserAssignments} 
          apiLoading={apiLoading}
          setLoading={setLoading}
        />
      )}
    </div>
  );
}

// Member Dashboard Component - FIXED VERSION
function MemberDashboard({ currentUser, passwords, personalPasswords, onLogout, isLoading, fetchPasswords, apiLoading, setLoading, lastUpdateCheck, setLastUpdateCheck }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('assigned');
  const [formState, setFormState] = useState({
    id: null,
    business_name: '',
    title: '',
    login_page_url: '',
    username: '',
    password: '',
    user_id: currentUser.id,
    isPersonal: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
  
  // ✅ FIXED: Filter assigned passwords to only show those with assignments
  const displayedPasswords = activeTab === 'assigned' 
    ? passwords.filter(p => p && p.password_type === 'assigned' && 
        (p.assigned_users && p.assigned_users.some(u => u.id === currentUser.id))) 
    : personalPasswords.filter(p => p && p.password_type === 'personal');

  const filteredPasswords = displayedPasswords.filter(p =>
    p && (
      (p.business_name && p.business_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.title && p.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (p.login_page_url && p.login_page_url.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  // Use ref for stable update checking function
  const checkForUpdatesRef = useRef(null);

  useEffect(() => {
  checkForUpdatesRef.current = async () => {
    if (!currentUser || isCheckingUpdates) return;

    setIsCheckingUpdates(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'check_updates',
        lastCheck: lastUpdateCheck,
        userId: currentUser.id,
        userRole: currentUser.role,
        teamLeadId: null
      });
      
      if (response.data && response.data.success) {
        // Check specifically for assignment updates
        const hasAssignmentUpdates = response.data.updates && response.data.updates.some(update => 
          update.table_name === 'password_assignments' || 
          update.table_name === 'personal_password_assignments'
        );
        
        const hasPasswordUpdates = response.data.updates && response.data.updates.some(update => 
          update.table_name === 'passwords' || 
          update.table_name === 'personal_passwords'
        );

        // Only refresh if there are relevant updates
        if (hasAssignmentUpdates || hasPasswordUpdates) {
          console.log('Member: Assignment or password updates detected, refreshing data...');
          await fetchPasswords();
        }
        
        // Always update the timestamp
        setLastUpdateCheck(response.data.currentTime || new Date().toISOString());
      }
    } catch (error) {
      console.error("Member: Error checking for updates:", error);
    } finally {
      setIsCheckingUpdates(false);
    }
  };
}, [currentUser, lastUpdateCheck, fetchPasswords, setLastUpdateCheck, isCheckingUpdates]);

  // Set up interval for update checking - SIMPLIFIED VERSION
  useEffect(() => {
    if (!currentUser) return;

    let interval;

    // Start checking after a short delay
    const initialTimer = setTimeout(() => {
      interval = setInterval(() => {
        if (checkForUpdatesRef.current) {
          checkForUpdatesRef.current();
        }
      }, UPDATE_CHECK_INTERVAL);
    }, 5000); // Start checking after 5 seconds
    
    // Set up visibility change listener
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, reduce check frequency
        clearInterval(interval);
        interval = setInterval(() => {
          if (checkForUpdatesRef.current) {
            checkForUpdatesRef.current();
          }
        }, BACKGROUND_UPDATE_CHECK_INTERVAL);
      } else {
        // Tab is visible, use normal frequency
        clearInterval(interval);
        interval = setInterval(() => {
          if (checkForUpdatesRef.current) {
            checkForUpdatesRef.current();
          }
        }, UPDATE_CHECK_INTERVAL);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      clearTimeout(initialTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Rest of the component functions remain the same...
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleAddPersonalPassword = async (e) => {
    e.preventDefault();
    setLoading(isEditing ? 'updatePassword' : 'addPassword', true);
    
    try {
      const action = isEditing ? 'update_personal_password' : 'add_personal_password';
      
      const payload = {
        action: action,
        businessName: formState.business_name,
        title: formState.title,
        loginPageUrl: formState.login_page_url,
        username: formState.username,
        password: formState.password,
        userId: currentUser.id,
      };
      
      if (isEditing) {
        payload.id = formState.id;
      }
      
      const response = await axios.post(API_URL, payload);
      if (response.data.success) {
        await fetchPasswords();
        setIsEditing(false);
        setFormState({
          id: null, business_name: '', title: '', login_page_url: '', username: '', password: '',
          user_id: currentUser.id, isPersonal: true
        });
        setError('');
        setShowForm(false);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError("Error saving password");
      console.error("Error saving password:", error);
    } finally {
      setLoading(isEditing ? 'updatePassword' : 'addPassword', false);
    }
  };

  const handleEditClick = (password, isPersonal = false) => {
    setFormState({
        id: password.id,
        business_name: password.business_name,
        title: password.title,
        login_page_url: password.login_page_url,
        username: password.username,
        password: password.password,
        user_id: currentUser.id,
        isPersonal: isPersonal
    });
    setIsEditing(true);
    setShowForm(true);
    setError('');
  };

  const handleDeletePassword = async (passwordId, isPersonal = false) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this password?");
    if (isConfirmed) {
      setLoading('deletePassword', true);
      try {
        const response = await axios.post(API_URL, { 
          action: 'delete_password', 
          id: passwordId,
          isPersonal: isPersonal
        });
        if (response.data.success) {
          await fetchPasswords();
          setError('');
        } else {
          setError(response.data.message);
        }
      } catch (error) {
        setError("Error deleting password");
        console.error("Error deleting password:", error);
      } finally {
        setLoading('deletePassword', false);
      }
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setFormState({
      id: null, business_name: '', title: '', login_page_url: '', username: '', password: '',
      user_id: currentUser.id, isPersonal: true
    });
    setError('');
    setShowForm(false);
  };
  
  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center space-x-3 mb-4 sm:mb-0">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 p-2 rounded-lg">
            <Key className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Your Password Vault</h1>
            <p className="text-sm text-gray-500">Welcome, {currentUser.name}!</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <LogOut size={16} className="mr-1" /> Logout
        </button>
      </div>
      
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('assigned')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${activeTab === 'assigned' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Shield className="inline-block mr-2" size={16} /> Assigned
        </button>
        <button
          onClick={() => setActiveTab('personal')}
          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${activeTab === 'personal' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Lock className="inline-block mr-2" size={16} /> Personal
        </button>
      </div>
      
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder={`Search your ${activeTab} passwords...`} 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
          />
        </div>
      </div>
      
      {activeTab === 'personal' && !showForm && (
        <div className="mb-6">
          <LoadingButton
            onClick={() => setShowForm(true)}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-medium py-3 px-4 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
          >
            <Plus className="mr-2" size={20} /> Add Personal Password
          </LoadingButton>
        </div>
      )}

      {showForm && (
        <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {isEditing ? 'Edit Personal Password' : 'Add Personal Password'}
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Personal</span>
            </h3>
            <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleAddPersonalPassword} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" name="business_name" value={formState.business_name} onChange={handleInputChange} placeholder="Business Name" required className="p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              <input type="text" name="title" value={formState.title} onChange={handleInputChange} placeholder="Title" required className="p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              <input type="url" name="login_page_url" value={formState.login_page_url} onChange={handleInputChange} placeholder="Login Page URL" required className="p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent md:col-span-2" />
              <input type="text" name="username" value={formState.username} onChange={handleInputChange} placeholder="Username" required className="p-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
              
              <div className="relative">
                <input 
                  type={showPasswordInput ? "text" : "password"}
                  name="password" 
                  value={formState.password} 
                  onChange={handleInputChange} 
                  placeholder="Password" 
                  required 
                  className="w-full p-2.5 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordInput(!showPasswordInput)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  tabIndex="-1"
                >
                  {showPasswordInput ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            {error && <p className="text-red-500 text-sm">{error}</p>}
            
            <div className="flex justify-end space-x-3">
            <button 
              type="button"
              onClick={cancelEdit}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            
            <LoadingButton
              type="submit" 
              isLoading={apiLoading.addPassword || apiLoading.updatePassword}
              loadingText={isEditing ? "Updating..." : "Adding..."}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              {isEditing ? (
                <>
                  <Save className="mr-2" size={16} />
                  Update Password
                </>
              ) : (
                <>
                  <Plus className="mr-2" size={16} />
                  Add Personal Password
                </>
              )}
            </LoadingButton>
            </div>
          </form>
        </div>
      )}
      
      {/* FIXED: Always show passwords immediately, don't wait for loading state */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredPasswords.length > 0 ? (
          filteredPasswords.map((p) => (
            <PasswordCard 
              key={p.id} 
              password={p} 
              isAdmin={false} 
              isTeamLead={false}
              showOwnerInfo={false}
              onEdit={handleEditClick}
              onDelete={handleDeletePassword}
              isPersonal={activeTab === 'personal'}
            />
          ))
        ) : (
          <div className="col-span-full text-center py-10">
            <Key className="mx-auto text-gray-300 mb-3" size={40} />
            <p className="text-gray-500">
              {searchTerm 
                ? `No ${activeTab} passwords found matching "${searchTerm}"` 
                : `No ${activeTab} passwords found.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Main App component with Router
export default function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
        </Routes>
      </div>
    </Router>
  );
}
